import {
	CalendarClock,
	Inbox,
	Layers,
	Moon,
	Repeat,
	Sunrise,
	Target,
	Timer,
	Wallet,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { BillsDue } from "@/components/bills-due";
import { BlockStartButton } from "@/components/focus/block-start-button";
import { FocusButton } from "@/components/focus/focus-button";
import { HabitCheck } from "@/components/habit-check";
import { MustWinStar } from "@/components/must-win-star";
import { EmptyState } from "@/components/empty-state";
import { PageContainer } from "@/components/page-container";
import { StatCard } from "@/components/stat-card";
import { TaskRow } from "@/components/task-row";
import { useAllowance } from "@/hooks/use-finance";
import { useActiveFocus } from "@/hooks/use-focus";
import { useHabitStatuses, useReview } from "@/hooks/use-habits";
import { useDueCards } from "@/hooks/use-study";
import { useNow } from "@/hooks/use-now";
import { useCarriedOver, useDayTasks, useInboxTasks, useNextBlock } from "@/hooks/use-tasks";
import type { Task } from "@/lib/db";
import { formatClock, remainingMs } from "@/lib/focus-timer";
import { formatUGX } from "@/lib/money";
import { MAX_MUST_WINS, setDone } from "@/lib/planner";
import { formatDay, formatInZone, localDateKey } from "@/lib/time";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

function DoneCheckbox({ task }: { task: Task }) {
	const done = task.status === "done";
	return (
		<label className="flex size-11 shrink-0 cursor-pointer items-center justify-center md:size-9">
			<Checkbox
				className="size-5"
				checked={done}
				aria-label={`${done ? "Mark not done" : "Mark done"}: ${task.title}`}
				onCheckedChange={async (v) => {
					const { next } = await setDone(task.id, v === true);
					if (next?.due_date)
						toast.success(`Next: ${formatDay(next.due_date)}`, { description: next.title });
				}}
			/>
		</label>
	);
}

export default function TodayPage() {
	// The day key is fixed for the life of the page; a reload after midnight
	// picks up the new day, and yesterday's leftovers show as carried over.
	const [now] = useState(() => new Date());
	const day = localDateKey(now);
	const tasks = useDayTasks(day);
	const inbox = useInboxTasks();
	const carried = useCarriedOver(day);
	const next = useNextBlock(now, day);
	const allowance = useAllowance(day);
	const focus = useActiveFocus();
	// Cards become due as the day goes on, so this clock keeps ticking (once a minute).
	const cardClock = useNow(true, 60_000);
	const dueCards = useDueCards(cardClock);
	const focusNow = useNow(!!focus);
	const morning = useReview("morning", day);
	const evening = useReview("evening", day);
	// Rituals: morning until done; evening from 17:00 Kampala until done.
	const ritual =
		morning !== undefined && typeof morning?.summary?.completed_at !== "string"
			? "morning"
			: evening !== undefined &&
				  typeof evening?.summary?.completed_at !== "string" &&
				  Number(formatInZone(now, "H")) >= 17
				? "evening"
				: null;
	const habits = (useHabitStatuses(day) ?? []).filter((s) => !s.habit.archived && s.streak.due);
	const habitsDone = habits.filter((s) => s.streak.periodMet).length;

	const mustWins = (tasks ?? []).filter((t) => t.must_win_date === day);
	const rest = (tasks ?? []).filter((t) => t.must_win_date !== day);
	const full = mustWins.length >= MAX_MUST_WINS;
	const winsDone = mustWins.filter((t) => t.status === "done").length;

	const row = (t: Task) => (
		<TaskRow
			key={t.id}
			task={t}
			showDue={false}
			leading={<DoneCheckbox task={t} />}
			actions={
				<>
					{t.status === "todo" && <FocusButton task={t} />}
					<MustWinStar task={t} day={day} full={full} />
				</>
			}
		/>
	);

	return (
		<PageContainer title="Today" description={formatInZone(now, "EEEE d MMMM")}>
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<StatCard
					icon={Target}
					label="Must-wins"
					value={`${winsDone} / ${mustWins.length || MAX_MUST_WINS}`}
					sub={mustWins.length ? "Done today" : "Pick up to 3 below"}
				/>
				<StatCard
					icon={CalendarClock}
					label="Next block"
					value={next ? formatInZone(new Date(next.start_at), "HH:mm") : "—"}
					sub={next ? next.title || "Time block" : "Nothing scheduled"}
				/>
				<StatCard
					icon={Wallet}
					label="Daily allowance"
					value={allowance?.budget ? formatUGX(allowance.allowance) : "UGX —"}
					sub={
						allowance?.budget
							? `${formatUGX(Math.max(0, allowance.budget - allowance.spent))} left this month`
							: "Set a budget in Money"
					}
				/>
				<StatCard
					icon={Repeat}
					label="Habits"
					value={habits.length ? `${habitsDone} / ${habits.length}` : "—"}
					sub={
						habits.length
							? habitsDone === habits.length
								? "All done today"
								: "Tap below to check off"
							: "Add habits on the Habits page"
					}
				/>
			</div>

			{(!!carried?.length || !!inbox?.length) && (
				<Link
					to="/inbox"
					className="bg-card hover:bg-accent mt-4 flex min-h-11 items-center gap-2 rounded-xl border px-4 py-2 text-sm"
				>
					<Inbox className="text-primary size-4" />
					<span>
						{[
							carried?.length && `${carried.length} carried over from earlier`,
							inbox?.length && `${inbox.length} in the inbox to sort`,
						]
							.filter(Boolean)
							.join(" · ")}
					</span>
				</Link>
			)}

			{focus && (
				<Link
					to="/focus"
					className="bg-primary text-primary-foreground mt-4 flex min-h-11 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium"
				>
					<Timer className="size-4" />
					Focus in progress: {formatClock(remainingMs(focus, focusNow))}
				</Link>
			)}

			{!!dueCards?.length && (
				<Link
					to="/study?review=1"
					className="bg-card hover:bg-accent mt-4 flex min-h-11 items-center gap-2 rounded-xl border px-4 py-2 text-sm"
				>
					<Layers className="text-primary size-4" />
					<span>
						{dueCards.length} card{dueCards.length === 1 ? "" : "s"} due to review
					</span>
				</Link>
			)}

			{next && next.kind !== "break" && next.kind !== "other" && (
				<div className="bg-card mt-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-2">
					<p className="min-w-0 truncate text-sm">
						<span className="text-muted-foreground">Next: </span>
						{formatInZone(new Date(next.start_at), "HH:mm")} {next.title || "Time block"}
					</p>
					<BlockStartButton block={next} />
				</div>
			)}

			{ritual && (
				<Link
					to={`/reviews?start=${ritual}`}
					className="bg-primary/10 text-primary hover:bg-primary/15 mt-4 flex min-h-11 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium"
				>
					{ritual === "morning" ? <Sunrise className="size-4" /> : <Moon className="size-4" />}
					{ritual === "morning" ? "Start your morning ritual" : "Time for the evening shutdown"}
				</Link>
			)}

			<BillsDue day={day} />

			{habits.length > 0 && (
				<section aria-labelledby="habits-heading" className="mt-4">
					<h2 id="habits-heading" className="mb-2 text-sm font-semibold">
						Habits
					</h2>
					<ul className="flex flex-wrap gap-2" aria-label="Habits due">
						{habits.map((s) => (
							<li
								key={s.habit.id}
								className={cn(
									"bg-card flex items-center gap-2 rounded-full border py-1 pr-4 pl-1",
									s.streak.periodMet && "text-muted-foreground",
								)}
							>
								<HabitCheck status={s} day={day} />
								<span className="text-sm">{s.habit.name}</span>
							</li>
						))}
					</ul>
				</section>
			)}

			<div className="mt-4 grid gap-4 lg:grid-cols-2">
				<section aria-labelledby="must-wins-heading">
					<h2 id="must-wins-heading" className="mb-2 text-sm font-semibold">
						Must-wins
					</h2>
					{mustWins.length ? (
						<ul className="bg-card divide-y rounded-xl border" aria-label="Must-wins">
							{mustWins.map(row)}
						</ul>
					) : (
						<EmptyState
							icon={Target}
							title="No must-wins yet"
							description="Tap the star on up to three of today's tasks: the ones that make the day a win."
						/>
					)}
				</section>
				<section aria-labelledby="today-heading">
					<h2 id="today-heading" className="mb-2 text-sm font-semibold">
						Also today
					</h2>
					{rest.length ? (
						<ul className="bg-card divide-y rounded-xl border" aria-label="Today's tasks">
							{rest.map(row)}
						</ul>
					) : (
						<EmptyState
							icon={CalendarClock}
							title="Nothing else today"
							description="Press Q to add a task, or move some from the inbox."
						/>
					)}
				</section>
			</div>
		</PageContainer>
	);
}
